import { IPatientAppointmentModel } from "@/Appointments/Models/UI/IPatientAppointmentModel";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { PatientAppointmentListItem } from "@/Common/Models/Appointments/PatientAppointmentListItem";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientHistoryAppointmentHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.History.Appointment_ChangeAppointmentShown, this.ChangeAppointmentShown);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_CriteriaClick, this.CriteriaClick);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_LoadPatientAppointment, this.LoadPatientAppointment);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_ShowSelectedAppointment, this.ShowSelectedAppointment);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_SearchText, this.SearchText);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_FromDateChanged, this.FromDateChanged);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_ToDateChanged, this.ToDateChanged);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_OnPrintLabelClick, this.onPrintLabelClick);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_OnPrintHistoryClick, this.OnPrintHistoryClick);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_CancelModifyAppointment, this.CancelModifyAppointment);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_ModifySelectedAppointment, this.OpenModifyPopup);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_SaveModifyAppointment, this.SaveModifyAppointmentCheck);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_ConfirmGroupStateEdit, this.SaveModifyAppointment);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_StateConfirmCheck, this.SaveModifyAppointment);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_ShowOnlyCurrent, this.ToggleAppointmentDisplayMode);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_OnSiteChange, this.OnSiteChange);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_ShowQuestionnaire, this.showQuestionnaire);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_OnQuestionnaireSubmitted, this.onQuestionnaireSubmitted);
        this.RegisterOperation(LocalPatientOperations.History.Appointment_PrintCertificate, this.onPrintCertificate);
    }

    private OnSiteChange(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];
        var siteId = request.Get<string>("value");
        appointmentModel = appointmentModel.set("SelectedSiteId", siteId);

        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        store.PatientUIModels[request.PatientId] = patient;

        store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
    }

    private ToggleAppointmentDisplayMode(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        var currentState = appointmentModel.get("ShowOnlyCurrent");
        appointmentModel = appointmentModel.set("ShowOnlyCurrent", !currentState);
        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        store.PatientUIModels[request.PatientId] = patient;

        if (appointmentModel.get("IsCriteriaExpanded") === true)
            store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
        else
            store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), null, null, "");
    }

    private updateAppointmentModelFromLocalState(request: LocalEvent, appointmentModel: IPatientAppointmentModel): IPatientAppointmentModel {
        const stateLookup = cacheStore.FindLookupByLookupCode(request.Get("appointmentState"));

        return appointmentModel
            .set("Subject", request.Get("subject"))
            .set("SelectedAppointmentStateCode", stateLookup.LookupCode)
            .set("SelectedAppointmentStateId", stateLookup.LookupID)
            .set("NotesForEdit", request.Get("notes"))
            .set("IsPaid", request.Get("isPaid"))
            .set("NotifyPatient", request.Get("isNotifyPatient"))
            .set("NotifyPatientManualOverride", request.Get("isNotifyManualOverride"))
            .set("NotifyPatientDisabled", request.Get("isNotifyPatient"))
            .set("IsTeleconsultation", request.Get("isTeleconsultation"));
    }

    private SaveModifyAppointmentCheck(request: LocalEvent, store: PatientStore): void {
        let appointmentModel = this.updateAppointmentModelFromLocalState(request, store.PatientHistoryAppointmentsModels[request.PatientId]);

        let appointment: PatientAppointmentListItem = ArrayHelper.Find(
            appointmentModel.get("AppointmentList").toArray(),
            (x: PatientAppointmentListItem) => x.Model.AppointmentId == appointmentModel.get("SelectedAppointmentId")
        );

        let newStateLookup = cacheStore.GetLookupById(appointmentModel.get("SelectedAppointmentStateId"));
        let currentStateLookup = cacheStore.GetLookupById(appointment.Model.StateId);
        let cancelledLookup = cacheStore.FindLookupByLookupCode("CANCELED", "SITEMGMT.APPOINTMENTS.STATE");
        let cancelledByPatientLookup = cacheStore.FindLookupByLookupCode("CANCELLED BY PATIENT", "SITEMGMT.APPOINTMENTS.STATE");

        if (((newStateLookup.LookupID != currentStateLookup.LookupID && (newStateLookup.LookupID == cancelledLookup.LookupID || newStateLookup.LookupID == cancelledByPatientLookup.LookupID)) // not cancelled to cancelled
            ||
            ((currentStateLookup.LookupID == cancelledByPatientLookup.LookupID || currentStateLookup.LookupID == cancelledLookup.LookupID) && newStateLookup.LookupID != cancelledLookup.LookupID && newStateLookup.LookupID != cancelledByPatientLookup.LookupID)) //  cancelled to not cancelled
            && (appointment.Model.ComboId > 0 && !IsNullOrWhiteSpace(appointment.Model.ComboName))
        ) {
            // show cancel group appointment warning popup
            appointmentModel = appointmentModel.set("IsComboAppointmentChangePopupVisible", true);
            appointmentModel = appointmentModel.set("OpenModifyPopup", false);
            store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        }
        else {
            store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
            this.SaveModifyAppointment(request, store);
        }
    }

    private SaveModifyAppointment(request: LocalEvent, store: PatientStore): void {
        let appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        let stateLookup = cacheStore.FindLookupByLookupCode(appointmentModel.get("SelectedAppointmentStateCode"));

        store.ActionsHistory.SaveModificationAppointment(
            appointmentModel.get("SelectedAppointmentId"),
            appointmentModel.get("SelectedAppointmentStateId"),
            stateLookup.GetValue(),
            appointmentModel.get("NotesForEdit"),
            appointmentModel.get("IsPaid"),
            stateLookup.LookupCode,
            appointmentModel.get("Subject"),
            !appointmentModel.get("NotifyPatient"),
            appointmentModel.get("IsTeleconsultation")
        );

        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
    }

    private OpenModifyPopup(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        var appointmentState = appointmentModel.get("AppointmentStates").toArray().filter(x => x.LookupID == appointmentModel.get("StateId"));
        if (appointmentState && appointmentState.length > 0) {
            appointmentModel = appointmentModel.set("SelectedAppointmentStateCode", appointmentState[0].LookupCode);
            appointmentModel = appointmentModel.set("SelectedAppointmentStateId", appointmentState[0].LookupID);
        }

        appointmentModel = appointmentModel.set("NotesForEdit", "");
        appointmentModel = appointmentModel.set("OpenModifyPopup", true);
        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        store.PatientUIModels[request.PatientId] = patient;
    }

    private CancelModifyAppointment(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        appointmentModel = appointmentModel.set("OpenModifyPopup", false);
        appointmentModel = appointmentModel.set("IsComboAppointmentChangePopupVisible", false);
        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        store.PatientUIModels[request.PatientId] = patient;
    }

    private onPrintLabelClick(request: LocalEvent, store: PatientStore): void {
        var patientModel = store.PatientModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        var patientNameString = patientModel.GetFormattedNameWithPrefix();
        var employeeNameString = appointmentModel.get("With");
        var appointmentDate = appointmentModel.get("StartDateForPrint");

        var site = cacheStore.GetSiteById(appointmentModel.get("SiteId"));
        var clinicNameString = site.Model.ClinicalName;
        var clinicAddressString = (site.Model.Adresses[0]) ? site.Model.Adresses[0].StreetAddress + ", " + site.Model.Adresses[0].ZipCode + ", " + site.Model.Adresses[0].City : "";

        var PhoneNumber = (site.Model.Phones && site.Model.Phones.length > 0) ? cacheStore.GetPhoneByType(site.Model.Phones, "PHONE", null, true) : "";
        var sitePhone = "";
        if (!IsNullOrWhiteSpace(PhoneNumber))
            sitePhone = "Tel: " + PhoneNumber;

        store.ActionsHistory.PrintAppointmentLabel(patientNameString, employeeNameString, appointmentDate, clinicNameString, clinicAddressString, sitePhone);
    }

    private OnPrintHistoryClick(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        store.ActionsHistory.PrintPatientAppointmentHistory(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
    }

    private FromDateChanged(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        appointmentModel = appointmentModel.set("DateFrom", request.Get<Date>("value"));

        appointmentModel = appointmentModel.set("ShowDetails", false);

        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        store.PatientUIModels[request.PatientId] = patient;

        store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
    }

    private ToDateChanged(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        appointmentModel = appointmentModel.set("DateTo", request.Get<Date>("value"));

        appointmentModel = appointmentModel.set("ShowDetails", false);

        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        store.PatientUIModels[request.PatientId] = patient;

        store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
    }

    public SearchText(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];
        var searchText = request.Get<string>("value");

        appointmentModel = appointmentModel.set("SearchText", searchText);
        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        store.PatientUIModels[request.PatientId] = patient;

        if (appointmentModel.get("IsCriteriaExpanded") === true)
            store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), searchText, appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
        else
            store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), searchText, null, null, "");
    }

    private ShowSelectedAppointment(request: LocalEvent, store: PatientStore): void {
        const appointment = request.Get<PatientAppointmentListItem>("appointment");

        let appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId]
            .set("Subject", appointment.Model.Subject)
            .set("SiteName", appointment.Model.SiteName)
            .set("With", appointment.Model.With)
            .set("Room", appointment.Model.Room)
            .set("ServiceDefinitionId", appointment.Model.ServiceDefinitionId)
            .set("ComboId", appointment.Model.ComboId)
            .set("StartTime", appointment.Model.StartTimeFullDate)
            .set("EndTime", appointment.Model.EndTimeFullDate)
            .set("StateName", appointment.Model.StateName)
            .set("StateId", appointment.Model.StateId)
            .set("Notes", appointment.Model.Notes)
            .set("SiteId", appointment.Model.SiteId)
            .set("StartDateForPrint", appointment.Model.StartDate)
            .set("IsPaid", appointment.Model.IsPaid)
            .set("IsTeleconsultation", appointment.Model.IsTeleconsultation)
            .set("SelectedAppointmentId", appointment.Model.AppointmentId)
            .set("QuestionnaireStatusInfo", appointment.Model.QuestionnaireStatusInfo)
            .set("ShowDetails", true)
            .set("SelectedAppointmentStateId", appointment.Model.StateId)
            .set("CanadianTriageAndAcuityScore", appointment.Model.CanadianTriageAndAcuityScore)
            .set("SelectedAppointmentStateCode", cacheStore.GetLookupById(appointment.Model.StateId).LookupCode)
            .set("IsReadOnly", cacheStore.ClinicSettings.IsCalendarReadOnly || appointment.Model.IsReadOnly);

        let patient = patientStore.getCurrentPatient();

        let IsNotifyImpossible = patient.Emails == null || patient.Emails.length == 0;
        let ForceEnabled = appointment.Model.IsPatientNotificationsEnabled == true;

        if (IsNotifyImpossible == true) {
            appointmentModel = appointmentModel
                .set("NotifyPatient", false)
                .set("NotifyPatientDisabled", true);
        }
        else if (ForceEnabled == true) {
            appointmentModel = appointmentModel
                .set("NotifyPatient", true)
                .set("NotifyPatientDisabled", true);
        }
        else {
            appointmentModel = appointmentModel
                .set("NotifyPatient", false)
                .set("NotifyPatientDisabled", false);
        }

        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
    }

    private ChangeAppointmentShown(request: LocalEvent, store: PatientStore): void {
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];
        if (appointmentModel.get("IsCriteriaExpanded") === true)
            store.ActionsHistory.LoadPatientAppointment(request.PatientId, appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
        else
            store.ActionsHistory.LoadPatientAppointment(request.PatientId, appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), null, null, "");
    }

    public CriteriaClick(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];
        var open = appointmentModel.get("IsCriteriaExpanded");

        appointmentModel = appointmentModel.set("IsCriteriaExpanded", !open);

        var employeeSite = sessionStore.getCurrentEmployee().getActiveSite().Model.Id;
        appointmentModel = appointmentModel.set("SelectedSiteId", employeeSite);

        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
        store.PatientUIModels[request.PatientId] = patient;

        if (appointmentModel.get("IsCriteriaExpanded") === true)
            store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), appointmentModel.get("DateFrom"), appointmentModel.get("DateTo"), appointmentModel.get("SelectedSiteId"));
        else
            store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), null, null, "");
    }

    public LoadPatientAppointment(request: LocalEvent, store: PatientStore): void {
        var patient = store.PatientUIModels[request.PatientId];
        var appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];
        store.ActionsHistory.LoadPatientAppointment(patient.get("PatientContactId"), appointmentModel.get("ShowOnlyCurrent"), appointmentModel.get("SearchText"), null, null, "");
    }

    public showQuestionnaire(request: LocalEvent, store: PatientStore): void {
        let showQuestionnaire = request.Get<boolean>("showQuestionnaire");
        let appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];

        appointmentModel = appointmentModel.set("ShowQuestionnaire", showQuestionnaire);

        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
    }

    public onQuestionnaireSubmitted(request: LocalEvent, store: PatientStore): void {
        let appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];
        let questionnaire = new QuestionnaireStatusInfo(appointmentModel.get("QuestionnaireStatusInfo"));
        questionnaire.Result = request.Get<boolean>("result");

        let appointmentList = appointmentModel.get("AppointmentList")?.mutate(
            x => x.Model.QuestionnaireStatusInfo = questionnaire,
            x => x.Model.QuestionnaireStatusInfo?.QuestionnaireInstanceId == questionnaire.QuestionnaireInstanceId
        );

        appointmentModel = appointmentModel
            .set("ShowQuestionnaire", false)
            .set("QuestionnaireStatusInfo", questionnaire)
            .set("AppointmentList", appointmentList)
            .set("AppointmentListLoaded", true);

        store.PatientHistoryAppointmentsModels[request.PatientId] = appointmentModel;
    }

    private onPrintCertificate(request: LocalEvent, store: PatientStore): void {
        const patient = store.PatientUIModels[request.PatientId];
        const patientContactId = patient.get("PatientContactId");

        const appointmentModel = store.PatientHistoryAppointmentsModels[request.PatientId];
        const appointmentDate = appointmentModel.get("StartDateForPrint");
        const siteId = appointmentModel.get("SiteId");

        store.ActionsHistory.printAppointmentCertificate(appointmentDate, siteId, patientContactId);
    }
}

export { PatientHistoryAppointmentHandler };
